import { AlertTriangle, ChevronRight, ChevronUp, Info, Pencil } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { standLabel } from '../onderdelen/FactuurRij'
import { FoutVak, Kiezer, Kop, Laden, Vraag } from '../onderdelen/ui'
import { afgehandeld, vier } from '../onderdelen/Klaar'
import { bedragVeld, BtwBewerker, Bewerker, leesBedrag, naarDatumVeld, uitDatumVeld, type Soort } from '../onderdelen/Aanpassen'
import { api, Fout } from '../lib/api'
import { useFactuur, vergeetFactuur } from '../lib/factuur'
import { afrondenNietVanMij, buitenDeRoute, KNOPNAAM, ligtBij, STAPNAAM, tekendeZelfAl, volgendeStap, type Handtekening } from '../lib/keten'
import { btwVan, datumKort, geld, inclusief, moment } from '../lib/opmaak'
import { ga, terug } from '../lib/router'
import { laadWerk, leesVoorkeuren, lees, meld, useStaat } from '../lib/staat'
import { volgendeVoorMij } from '../lib/bakken'
import type { Expense, Kan, Keuzes, Persoon, Wijziging } from '../lib/types'
import { ontbreekt, standVan } from '../lib/werklijst'
import { geenFactuur, inDispuut } from '../lib/zicht.ts'

/* ===========================================================================
 *  Eén factuur
 *
 *  Bovenin het raster van Blue10 (relatie, status, bron; de drie datums; de
 *  nummers en de conditie), dan de bedragen, dan Originelen, Boekingen en
 *  Tijdlijn. Onderin "Acties": een opmerking, bij wie hij moet komen
 *  (Gebruiker), Overslaan, en de knop met de actie die je kiest.
 *
 *  Welke actie er is, volgt uit lib/keten.ts (een letterlijke kopie van het
 *  dashboard), met de regels van de DATABASE voor dit bedrag
 *  (vier_ogen_nodig, afronding_nodig): het scherm laat dezelfde knop zien
 *  als het dashboard. Of het mag, beslist daarna de database -- ook hier.
 * =========================================================================== */

type Actie = 'tekenen' | 'exact' | 'doorzetten' | 'terugzetten' | 'afkeuren' | 'opmerking' | 'dispuut' | 'uitdispuut' | 'toch'

const BRON: Record<string, string> = { mail: 'E-mail', scan: 'Scan', app: 'Handmatig' }

const VOLGORDE: Handtekening[] = ['valideren', 'eerste', 'tweede', 'afronding']

export default function Factuur({ id }: { id: string }) {
  const { antwoord, laden, fout, ververs } = useFactuur(id)
  const ik = useStaat((s) => s.ik)

  const [opmerking, setOpmerking] = useState('')
  const [aan, setAan] = useState<Persoon | null>(null)
  const [actie, setActie] = useState<Actie | null>(null)
  const [kiezer, setKiezer] = useState<null | 'gebruiker' | 'actie' | 'terug' | 'grootboek' | 'conditie' | 'bedrijf'>(null)
  /* Aanpassen (20 okt 2026): welk veld er open staat, en de lijsten om uit te kiezen. */
  const [bewerk, setBewerk] = useState<null | {
    titel: string; uitleg?: string; soort: Soort; begin: string; leegMag?: boolean; bewaar: (v: string) => Promise<void>
  }>(null)
  const [btwOpen, setBtwOpen] = useState(false)
  const [keuzes, setKeuzes] = useState<Keuzes | null>(null)
  const [vraag, setVraag] = useState<null | {
    titel: string; uitleg?: string; label: string; verplicht: boolean; knop: string; gevaar?: boolean
    doe: (t: string) => Promise<void>
  }>(null)
  const [personen, setPersonen] = useState<Persoon[] | null>(null)
  const [bezig, setBezig] = useState(false)

  /* Een andere factuur: alles van de vorige eraf. */
  useEffect(() => { setOpmerking(''); setAan(null); setActie(null); setKiezer(null); setVraag(null); setBewerk(null); setBtwOpen(false); setKeuzes(null) }, [id])

  const bon = antwoord?.bon
  const regels = antwoord?.regelsVoorBedrag ?? { vierOgen: true, afronding: true }
  const stap = bon ? volgendeStap(bon, regels) : null
  const management = ik?.mag.management === true
  const wie = ik?.ik.id ?? ''
  /*
   * Wat er mag, met de regels van TW10 Online (zicht.ts), uitgerekend door de
   * functie (23 okt 2026). Een functie van daarvoor stuurt dit nog niet mee:
   * dan zoals het was (Naar Exact voor iedereen, geen dispuut).
   */
  const mogen: Kan | null = !bon ? null : antwoord?.kan ?? {
    dispuutZetten: false, uitDispuut: false, handBoeken: true, valideren: ik?.mag.valideren === true,
    inDispuut: inDispuut(bon), geenFactuur: geenFactuur(bon),
  }

  /* Wat kan er met deze factuur, voor mij, op dit moment? */
  const kan = useMemo(() => {
    if (!bon || !ik || !mogen) return null
    const doorMij = tekendeZelfAl(stap, wie)
    const buiten = buitenDeRoute(stap, wie)
    /* In dispuut staat hij stil (0240): niet tekenen en niet naar Exact, zoals in TW10 Online. */
    const stil = mogen.inDispuut

    let tekenenUit: string | null = null
    /* Valideren: met tw10.valideren overal, met het vinkje Valideren alleen bij dat bedrijf (0241). */
    if (stap?.stap === 'valideren' && !mogen.valideren) tekenenUit = 'Valideren doet wie voor dit bedrijf valideert.'
    /* Afronden doet alleen wie bij de afronding staat (0226) -- ook
       management niet, ook niet met een reden. */
    else if (afrondenNietVanMij(stap, wie)) tekenenUit = `Afronden doet alleen ${stap!.groepNaam.join(', ')}.`
    else if (doorMij && !management) tekenenUit = 'Je tekende deze factuur zelf al; de volgende handtekening moet van iemand anders komen.'
    else if (buiten && !management) tekenenUit = `Deze stap ligt bij ${stap!.groepNaam.join(', ')}.`

    /* Terugzetten: naar een stap die al gezet is, zoals Detail.tsx in TW10
       Online: wat er nu aan de beurt is en alles daarna valt af, en een
       handtekening die er niet staat, kun je niet terugzetten. */
    const gezet: Handtekening[] = []
    if (!bon.exactId && bon.status !== 'afgekeurd' && bon.status !== 'open') {
      const tot = bon.status === 'goedgekeurd' ? 4 : VOLGORDE.indexOf(stap?.stap ?? 'valideren')
      for (const s of VOLGORDE.slice(0, tot)) {
        if (bon.richting === 'verkoop' && s !== 'valideren') continue
        if (s === 'eerste' && !bon.eersteDoor) continue
        if (s === 'tweede' && !bon.tweedeDoor && bon.status !== 'goedgekeurd') continue
        gezet.push(s)
      }
    }

    return {
      doorMij, buiten, tekenenUit, stil,
      tekenen: !!stap && !stil,
      /* De afronding doorzetten kan niet meer: die ligt vast bij de afronder (0226). */
      doorzetten: !!stap && stap.stap !== 'valideren' && stap.stap !== 'afronding' && ik.mag.beslissen,
      terugzetten: gezet.length > 0 && ik.mag.beslissen ? gezet : null,
      afkeuren: bon.status !== 'afgekeurd' && !bon.exactId && ik.mag.beslissen,
      /* Goedgekeurd en nog niet in Exact (of Exact weigerde): opnieuw
         proberen -- alleen voor wie met de hand boekt (Boekhouden, of het
         vinkje Boeken voor dit bedrijf), zoals in TW10 Online. */
      exact: !stil && mogen.handBoeken && (bon.richting ?? 'inkoop') === 'inkoop' && bon.status === 'goedgekeurd' && !bon.exactId,
      dispuut: mogen.dispuutZetten,
      uitDispuut: mogen.uitDispuut,
      /* "Toch een factuur": wie over kosten beslist, zoals de knop in TW10 Online. */
      toch: mogen.geenFactuur && ik.mag.beslissen,
    }
  }, [bon, ik, stap, wie, management, mogen])

  /* De standaardactie: tekenen als dat kan, anders de eerste die er is. */
  const gekozen: Actie = actie
    ?? (kan?.tekenen && !kan.tekenenUit ? 'tekenen'
      : kan?.exact ? 'exact'
      : kan?.doorzetten ? 'doorzetten'
        : kan?.terugzetten ? 'terugzetten'
          : 'opmerking')

  const actieLabel = (a: Actie): string => {
    if (a === 'tekenen') return stap ? KNOPNAAM[stap.stap] : 'Opsturen'
    if (a === 'exact') return 'Naar Exact sturen'
    if (a === 'doorzetten') return 'Doorzetten'
    if (a === 'terugzetten') return 'Terugzetten'
    if (a === 'afkeuren') return 'Afkeuren'
    if (a === 'dispuut') return 'In dispuut zetten'
    if (a === 'uitdispuut') return 'Uit dispuut halen'
    if (a === 'toch') return 'Toch een factuur'
    return 'Opmerking plaatsen'
  }

  function volgende(): string | null {
    const ids = lees().lijst
    const i = ids.indexOf(id)
    if (i < 0) return null
    return ids.slice(i + 1).find((x) => x !== id) ?? null
  }

  function verder(na: 'gedaan' | 'overslaan') {
    vergeetFactuur(id)
    const v = volgende()
    if (v && (na === 'overslaan' || leesVoorkeuren().doorNaVolgende !== false)) {
      ga(`/factuur/${encodeURIComponent(v)}`, true)
    } else if (na === 'overslaan') {
      meld('Dit was de laatste in de lijst.', 'let-op')
    } else {
      terug('/todo')
    }
  }

  /*
   * Na tekenen, afkeuren, terugzetten of doorzetten: door naar de volgende
   * die op JOU wacht, en is er niets meer, het feest (22 okt 2026, TW10
   * Online). Casper: "als je klaar met een factuur bent, en je doorgestuurd
   * wordt naar de ander, dat dit ook enkel naar die van jouwzelf gebeurt."
   * Hier stond: de volgende in de lijst waar je vandaan kwam -- ook als die
   * op een ander wachtte.
   */
  async function naAfhandelen(wasValideren: boolean) {
    vergeetFactuur(id)
    afgehandeld()
    await laadWerk()
    const { werk, ik: ikNu, lijst } = lees()
    const v = werk && ikNu ? volgendeVoorMij(id, wasValideren, werk.bonnen, ikNu, lijst) : null
    if (!v) { vier(); terug('/todo'); return }
    if (leesVoorkeuren().doorNaVolgende !== false) ga(`/factuur/${encodeURIComponent(v.id)}`, true)
    else terug('/todo')
  }

  async function voerUit(werk: () => Promise<string>) {
    setBezig(true)
    const wasValideren = stap?.stap === 'valideren'
    try {
      const tekst = await werk()
      meld(tekst, 'ok')
      await naAfhandelen(wasValideren)
    } catch (e) {
      const f = e instanceof Fout ? e : null
      meld(f?.message ?? String(e), f?.code === 'veranderd' ? 'let-op' : 'fout')
      if (f?.code === 'veranderd') { vergeetFactuur(id); void ververs() }
    } finally {
      setBezig(false)
      setVraag(null)
    }
  }

  async function kiesGebruiker() {
    setKiezer('gebruiker')
    if (!personen) {
      try { setPersonen(await api.personen()) } catch (e) { meld(e instanceof Fout ? e.message : String(e), 'fout') }
    }
  }

  /* ---------------- de knop ---------------- */

  function tekenen(bon: Expense, zelfBeideWaarom?: string, buitenRouteWaarom?: string) {
    if (!stap || !kan) return
    /* Eerst de vragen, een tegelijk (zie Kostenposten.tsx: twee vensters
       over elkaar is geen keuze maar een hindernis). */
    if (kan.doorMij && management && zelfBeideWaarom === undefined) {
      setVraag({
        titel: 'Je tekende zelf al',
        uitleg: 'Vier ogen betekent twee verschillende mensen. Als management mag je toch tekenen, met een reden; die komt in de historie.',
        label: 'Waarom teken je allebei?', verplicht: true, knop: 'Toch tekenen',
        doe: async (t) => { setVraag(null); tekenen(bon, t, buitenRouteWaarom) },
      })
      return
    }
    if (kan.buiten && management && buitenRouteWaarom === undefined) {
      setVraag({
        titel: 'Buiten de route',
        uitleg: `Deze stap ligt bij ${stap.groepNaam.join(', ')}. Als management mag je hem toch zetten, met een reden.`,
        label: 'Waarom teken jij?', verplicht: true, knop: KNOPNAAM[stap.stap],
        doe: async (t) => { setVraag(null); tekenen(bon, zelfBeideWaarom, t) },
      })
      return
    }

    void voerUit(async () => {
      const uit = await api.tekenen({
        id, verwacht: bon.status, opmerking,
        ...(aan ? { aan: aan.id } : {}),
        ...(zelfBeideWaarom ? { zelfBeideWaarom } : {}),
        ...(buitenRouteWaarom ? { buitenRouteWaarom } : {}),
      })
      const naar = aan ? ` Hij ligt nu bij ${aan.naam}.` : ''
      if (uit.status === 'gevalideerd') return (bon.richting === 'verkoop' ? 'Gevalideerd. Hij staat klaar om te boeken.' : 'Gevalideerd.') + naar
      if (uit.status === 'eerste_akkoord') return 'Eerste handtekening gezet.' + naar
      if (uit.status === 'tweede_akkoord') return 'Tweede handtekening gezet. Hij wacht nu op de afronding.' + naar
      if (uit.status === 'goedgekeurd') {
        /* Niet meer wachten op Exact (22 okt 2026): de boekwekker neemt hem mee. Dezelfde zin als TW10 Online. */
        if (uit.exact?.onderweg) return 'Afgerond. Hij gaat binnen een kwartier vanzelf naar Exact; weigert Exact, dan krijg je een melding in de bel.'
        return uit.exact?.gelukt ? 'Afgerond en geboekt in Exact.' : `Afgerond. Nog niet in Exact: ${uit.exact?.reden ?? 'hij staat klaar op Naar Exact.'}`
      }
      return 'Gedaan.'
    })
  }

  function opsturen() {
    if (!bon || !kan) return
    if (gekozen === 'tekenen') {
      if (kan.tekenenUit) { meld(kan.tekenenUit, 'let-op'); return }
      const mist = stap?.stap === 'valideren' ? ontbreekt(bon) : []
      if (mist.length && !window.confirm(`Er ontbreekt iets:\n\n• ${mist.join('\n• ')}\n\nToch valideren?`)) return
      tekenen(bon)
      return
    }
    if (gekozen === 'exact') {
      setBezig(true)
      api.naarExact(id)
        .then((uit) => {
          vergeetFactuur(id)
          if (uit.gelukt) { meld('Geboekt in Exact.'); void laadWerk(); verder('gedaan') }
          else { meld(`Nog niet in Exact: ${uit.reden ?? 'onbekend'}`, 'let-op'); void ververs() }
        })
        .catch((e) => meld(e instanceof Fout ? e.message : String(e), 'fout'))
        .finally(() => setBezig(false))
      return
    }
    if (gekozen === 'doorzetten') {
      if (!aan) { meld('Kies eerst bij Gebruiker bij wie hij moet komen.', 'let-op'); void kiesGebruiker(); return }
      void voerUit(async () => {
        await api.doorzetten({ id, verwacht: bon.status, aan: aan.id, opmerking })
        return `Doorgezet naar ${aan.naam}.`
      })
      return
    }
    if (gekozen === 'terugzetten') { setKiezer('terug'); return }
    if (gekozen === 'dispuut' || gekozen === 'uitdispuut') { dispuutVraag(gekozen === 'dispuut'); return }
    if (gekozen === 'toch') { void tochEenFactuur(); return }
    if (gekozen === 'afkeuren') {
      setVraag({
        titel: 'Afkeuren',
        uitleg: 'De factuur gaat uit de stroom en wordt niet geboekt. Bedoel je "er klopt iets niet, maar het is geen afwijzing"? Kies dan Terugzetten.',
        label: 'Reden', verplicht: false, knop: 'Afkeuren', gevaar: true,
        doe: (reden) => voerUit(async () => {
          await api.afkeuren({ id, verwacht: bon.status, reden, opmerking })
          return 'Factuur afgekeurd.'
        }),
      })
      return
    }
    /* Alleen de opmerking. */
    if (!opmerking.trim()) { meld('Er staat nog niets in de opmerking.', 'let-op'); return }
    setBezig(true)
    api.notitie(id, opmerking)
      .then(() => { meld('Opmerking geplaatst.'); setOpmerking(''); vergeetFactuur(id); void ververs() })
      .catch((e) => meld(e instanceof Fout ? e.message : String(e), 'fout'))
      .finally(() => setBezig(false))
  }

  /* ---------------- in dispuut, en toch een factuur ---------------- */

  /*
   * In of uit dispuut (0240), zoals het venstertje in TW10 Online: erin met
   * een reden (verplicht), eruit met een opmerking (mag leeg). De database
   * (factuur_dispuut) beslist wie het mag en zegt anders waarom niet.
   */
  function dispuutVraag(erin: boolean) {
    setVraag({
      titel: erin ? 'In dispuut zetten' : 'Uit dispuut halen',
      uitleg: erin
        ? 'Zolang hij in dispuut staat, staat hij stil: niemand kan hem tekenen, hij gaat niet naar Exact en wordt niet betaald.'
        : 'Daarna gaat hij weer gewoon verder waar hij was.',
      label: erin ? 'Waarom? Bijvoorbeeld: creditnota gevraagd' : 'Opmerking, bijvoorbeeld: creditnota ontvangen',
      verplicht: erin,
      knop: erin ? 'In dispuut zetten' : 'Uit dispuut halen',
      doe: async (reden) => {
        try {
          await api.dispuut(id, erin, reden)
          meld(erin ? 'In dispuut gezet. Hij staat stil tot iemand hem eruit haalt.' : 'Uit dispuut gehaald. Hij gaat weer gewoon verder.')
          setVraag(null); setActie(null)
          vergeetFactuur(id)
          void ververs()
          void laadWerk()
        } catch (e) {
          meld(e instanceof Fout ? e.message : String(e), 'fout')
        }
      },
    })
  }

  /* "Toch een factuur" (0178): de AI-assistent zat ernaast; terug naar Valideren. */
  async function tochEenFactuur() {
    setBezig(true)
    try {
      await api.tochFactuur(id)
      meld('Terug in Valideren.')
      setActie(null)
      vergeetFactuur(id)
      void ververs()
      void laadWerk()
    } catch (e) {
      meld(e instanceof Fout ? e.message : String(e), 'fout')
    } finally {
      setBezig(false)
    }
  }

  /* ---------------- aanpassen ---------------- */

  /*
   * Wie mag: wie over kosten beslist (dezelfde regel als de database,
   * mag_kosten_beslissen), en niet meer zodra hij in Exact staat -- dan komt
   * een wijziging daar niet meer aan. De server controleert het opnieuw.
   */
  const magAanpassen = !!bon && !bon.exactId && ik?.mag.beslissen === true

  /** Opslaan met wat je zag erbij; bij "iemand was sneller" meteen de nieuwe stand ophalen. */
  async function sla(velden: Wijziging, was: Partial<Record<keyof Wijziging, unknown>>) {
    try {
      await api.wijzigen(id, velden, was)
    } catch (e) {
      if (e instanceof Fout && e.status === 409) { vergeetFactuur(id); void ververs() }
      throw e
    }
    vergeetFactuur(id)
    await ververs()
    void laadWerk()
    meld('Opgeslagen.')
  }

  async function haalKeuzes(): Promise<Keuzes | null> {
    if (keuzes) return keuzes
    try {
      const k = await api.keuzes(id)
      setKeuzes(k)
      return k
    } catch (e) {
      meld(e instanceof Fout ? e.message : String(e), 'fout')
      return null
    }
  }

  function open(titel: string, soort: Soort, begin: string, bewaar: (v: string) => Promise<void>, extra: { uitleg?: string; leegMag?: boolean } = {}) {
    if (!magAanpassen) return
    setBewerk({ titel, soort, begin, bewaar, ...extra })
  }

  /** Een keuze uit een lijst opslaan; een fout als melding, want de Kiezer is dan al dicht. */
  function kiesEnSla(velden: Wijziging, was: Partial<Record<keyof Wijziging, unknown>>) {
    void sla(velden, was).catch((e) => meld(e instanceof Error ? e.message : String(e), 'fout'))
  }

  /* ---------------- het scherm ---------------- */

  if (!antwoord || !bon) {
    return (
      <div className="scherm">
        <Kop terugNaar="/todo" terugLabel="To-do" titel="Factuur" />
        {fout ? <FoutVak tekst={fout} opnieuw={() => void ververs()} /> : laden && <Laden />}
      </div>
    )
  }

  const mist = standVan(bon) === 'aanvullen' || standVan(bon) === 'valideren' ? ontbreekt(bon) : []
  const actiesTeKiezen: { waarde: Actie; label: string; uit?: string; uitleg?: string }[] = [
    ...(kan?.tekenen ? [{ waarde: 'tekenen' as Actie, label: actieLabel('tekenen'), ...(kan.tekenenUit ? { uit: kan.tekenenUit } : {}) }] : []),
    ...(kan?.exact ? [{ waarde: 'exact' as Actie, label: 'Naar Exact sturen' }] : []),
    ...(kan?.doorzetten ? [{ waarde: 'doorzetten' as Actie, label: 'Doorzetten', uitleg: 'Zonder te tekenen naar de gekozen gebruiker' }] : []),
    ...(kan?.terugzetten ? [{ waarde: 'terugzetten' as Actie, label: 'Terugzetten' }] : []),
    ...(kan?.afkeuren ? [{ waarde: 'afkeuren' as Actie, label: 'Afkeuren' }] : []),
    ...(kan?.dispuut ? [{ waarde: 'dispuut' as Actie, label: 'In dispuut zetten', uitleg: 'Zet hem stil, bijvoorbeeld omdat er een creditnota gevraagd is' }] : []),
    ...(kan?.uitDispuut ? [{ waarde: 'uitdispuut' as Actie, label: 'Uit dispuut halen' }] : []),
    ...(kan?.toch ? [{ waarde: 'toch' as Actie, label: 'Toch een factuur', uitleg: 'Terug naar Valideren' }] : []),
    { waarde: 'opmerking', label: 'Opmerking plaatsen' },
  ]

  /*
   * Omgedraaide datums (zoals Detail.tsx, 20 okt 2026): de vervaldatum ligt
   * echt voor de factuurdatum. Niet bij een aanmaning -- daar is de oude
   * vervaldatum juist voorbij -- en niet bij een paar uur verschil.
   */
  const omgedraaid = !!bon.vervaldatum && !!bon.date && bon.vervaldatum < bon.date - 12 * 3600_000
    && bon.gelezen?.soort !== 'aanmaning'
  const melding = antwoord.exactMelding

  const m = magAanpassen
  const tekstOpslaan = (veld: 'supplier' | 'description' | 'factuurnummer', oud: string | undefined) => async (v: string) =>
    sla({ [veld]: v || null } as Wijziging, { [veld]: oud ?? null })
  const datumOpslaan = (veld: 'date' | 'vervaldatum', oud: number | undefined) => async (v: string) =>
    sla({ [veld]: uitDatumVeld(v) } as Wijziging, { [veld]: oud ?? null })
  const rekening = bon.grootboekCode
    ? `${bon.grootboekCode}${antwoord.grootboekNaam ? ' - ' + antwoord.grootboekNaam : ''}`
    : 'Nog geen'

  return (
    <div className="scherm factuur">
      <Kop terugNaar="/todo" terugLabel="To-do" titel="Factuur" />

      <div className="raster">
        <Vak waarde={bon.supplier.trim() || 'Onbekende relatie'} label={antwoord.bedrijfNaam ?? bon.administratie ?? '—'} zacht
          opKlik={m ? () => open('Leverancier', 'tekst', bon.supplier, tekstOpslaan('supplier', bon.supplier)) : undefined} />
        <Vak waarde={standLabel(bon)} label="Status" zacht />
        <Vak waarde={BRON[bon.source ?? 'app'] ?? 'Handmatig'} label="Bron type" />
        <Vak waarde={datumKort(bon.date)} label="Factuurdatum" zacht
          opKlik={m ? () => open('Factuurdatum', 'datum', naarDatumVeld(bon.date), datumOpslaan('date', bon.date),
            { uitleg: 'Gaat als boekdatum naar Exact en bepaalt in welke periode de kosten vallen.' }) : undefined} />
        <Vak waarde={datumKort(bon.vervaldatum)} label="Vervaldatum" zacht
          opKlik={m ? () => open('Vervaldatum', 'datum', naarDatumVeld(bon.vervaldatum), datumOpslaan('vervaldatum', bon.vervaldatum),
            { leegMag: true }) : undefined} />
        <Vak waarde={datumKort(bon.betaaldAt)} label="Betaaldatum" zacht />
        <Vak waarde={bon.factuurnummer || '-'} label="Factuurnr" zacht breekt
          opKlik={m ? () => open('Factuurnummer', 'tekst', bon.factuurnummer ?? '', tekstOpslaan('factuurnummer', bon.factuurnummer),
            { leegMag: true }) : undefined} />
        <Vak waarde={bon.exactNummer || '-'} label="Boekstuknr" zacht />
        <Vak waarde={bon.betaalconditieNaam || bon.betaalconditie || '-'} label="Conditie" zacht
          opKlik={m ? () => { void haalKeuzes().then((k) => k && setKiezer('conditie')) } : undefined} />
      </div>

      <dl className="bedragen">
        <Bedrag label="Kosten" waarde={geld(bon.amountExcl, bon.valuta)}
          opKlik={m ? () => open('Bedrag excl. btw', 'bedrag', bedragVeld(bon.amountExcl), async (v) => {
            const n = leesBedrag(v)
            if (n == null || Number.isNaN(n)) throw new Error('Vul een bedrag in.')
            await sla({ amountExcl: n }, { amountExcl: bon.amountExcl })
          }, { uitleg: 'Het bedrag zonder btw, zoals op de factuur.' }) : undefined} />
        <Bedrag label={`BTW${bon.vatPct != null ? ` (${bon.vatPct}%)` : ''}`} waarde={geld(btwVan(bon), bon.valuta)}
          opKlik={m ? () => setBtwOpen(true) : undefined} />
        <div className="totaal"><dt>Te betalen</dt><dd>{geld(inclusief(bon), bon.valuta)}</dd></div>
      </dl>

      <ul className="gegevens">
        <Gegeven label="Onderneming" waarde={antwoord.bedrijfNaam ?? bon.administratie ?? 'Nog geen'}
          opKlik={m && (ik?.bedrijven.length ?? 0) > 1 ? () => setKiezer('bedrijf') : undefined} />
        <Gegeven label="Grootboekrekening" waarde={rekening}
          opKlik={m ? () => { void haalKeuzes().then((k) => k && setKiezer('grootboek')) } : undefined} />
        <Gegeven label="Omschrijving" waarde={bon.description || '-'}
          opKlik={m ? () => open('Omschrijving', 'tekst', bon.description ?? '', tekstOpslaan('description', bon.description), { leegMag: true }) : undefined} />
        {/* Geen rekeningnummer: IBAN, btw-nummer en KvK komen uit Exact (Casper, 20 okt 2026). */}
        <Gegeven label="Valuta" waarde={bon.valuta || 'EUR'}
          opKlik={m ? () => open('Valuta', 'tekst', bon.valuta ?? '', async (v) => sla({ valuta: v || null }, { valuta: bon.valuta ?? null }),
            { leegMag: true, uitleg: 'Drie letters, zoals USD of GBP. Leeg is euro.' }) : undefined} />
      </ul>
      {bon.exactId && <p className="klein zacht midden">Deze factuur staat in Exact. Aanpassen doe je daar.</p>}
      {!bon.exactId && !m && <p className="klein zacht midden">Aanpassen kan wie over kosten beslist.</p>}

      <div className="pillen">
        <button className="pil" onClick={() => ga(`/factuur/${encodeURIComponent(id)}/originelen`)}>Originelen</button>
        <button className="pil" onClick={() => ga(`/factuur/${encodeURIComponent(id)}/boekingen`)}>Boekingen</button>
        <button className="pil" onClick={() => ga(`/factuur/${encodeURIComponent(id)}/tijdlijn`)}>Tijdlijn</button>
      </div>

      <div className="aandacht">
        {inDispuut(bon) && (
          /* In dispuut (0240): bovenaan, want zolang dit er staat gebeurt er niets met hem. */
          <div className="let let-dispuut" role="status">
            <AlertTriangle size={16} aria-hidden="true" />
            <span className="let-blok">
              <span><b>In dispuut</b> sinds {moment(bon.dispuutSinds)}{bon.dispuutDoorNaam ? ` door ${bon.dispuutDoorNaam}` : ''}: {bon.dispuutReden || 'zonder reden'}</span>
              <small>Hij staat stil: niet tekenen, niet boeken en niet betalen, tot iemand hem uit dispuut haalt.</small>
              {kan?.uitDispuut && <button type="button" className="let-knop" disabled={bezig} onClick={() => dispuutVraag(false)}>Uit dispuut halen</button>}
            </span>
          </div>
        )}
        {mogen?.geenFactuur && (
          /* Geen factuur volgens de AI-assistent of iemand (21 okt 2026): uit de lijsten, niet weg. */
          <div className="let let-let-op">
            <Info size={16} aria-hidden="true" />
            <span className="let-blok">
              <span>{bon.tochFactuur === false ? 'Iemand heeft dit als geen factuur weggezet.' : 'Volgens de AI-assistent is dit geen factuur (een plaatje, logo of nieuwsbrief).'} Het staat niet in Valideren.</span>
              {kan?.toch && <button type="button" className="let-knop" disabled={bezig} onClick={() => void tochEenFactuur()}>Toch een factuur</button>}
            </span>
          </div>
        )}
        {melding ? (
          /* Waarom hij niet naar Exact gaat: dezelfde zinnen als NaarExactMelding in TW10 Online. */
          <div className={`let ${melding.fout ? 'let-stuk' : 'let-let-op'}`}>
            <AlertTriangle size={16} aria-hidden="true" />
            <span className="let-blok">
              <b>{melding.kop}</b>
              <span>{melding.tekst}</span>
              <small>Wat te doen: {melding.doen}</small>
              {melding.ruw && (
                <details>
                  <summary>Wat Exact letterlijk zei</summary>
                  <span className="ruw">{melding.ruw}</span>
                </details>
              )}
            </span>
          </div>
        ) : bon.exactFout && antwoord.exactMelding === undefined && <Let soort="stuk">Exact weigerde: {bon.exactFout}</Let>}
        {omgedraaid && (
          <Let soort="stuk">De vervaldatum ligt voor de factuurdatum; ze zijn waarschijnlijk omgedraaid. Omdraaien doet wie boekt, in TW10 Online.</Let>
        )}
        {bon.status === 'afgekeurd' && <Let soort="stuk">Afgekeurd{bon.approvedByName ? ` door ${bon.approvedByName}` : ''}{bon.rejectReason ? `: ${bon.rejectReason}` : '.'}</Let>}
        {mist.length > 0 && <Let soort="let-op">{mist.join(' ')}</Let>}
        {stap && stap.stap !== 'valideren' && <Let soort="info">{ligtBij(stap).titel}</Let>}
        {standVan(bon) === 'lezen' && <Let soort="info">De AI-assistent leest deze factuur nog. Hier hoef je nu niets te doen.</Let>}
      </div>

      <section className="acties">
        <h2>Acties</h2>
        <textarea
          className="opmerking" placeholder="Opmerking" rows={2} maxLength={2000}
          value={opmerking} onChange={(e) => setOpmerking(e.target.value)}
        />

        <div className="acties-twee">
          <div>
            <button className="knop lucht" onClick={() => void kiesGebruiker()} disabled={bezig}>
              <span className="afkap">{aan ? aan.naam : '(Geen gebruiker)'}</span>
            </button>
            <div className="onderschrift">Gebruiker</div>
          </div>
          <div>
            <button className="knop lucht" onClick={() => verder('overslaan')} disabled={bezig}>Overslaan</button>
            <div className="onderschrift">Overslaan</div>
          </div>
        </div>

        <div className="splitknop">
          <button className="knop lucht hoofd" onClick={opsturen} disabled={bezig}>
            {bezig ? 'Bezig…' : actieLabel(gekozen)}
          </button>
          <button className="knop lucht pijl" onClick={() => setKiezer('actie')} disabled={bezig} aria-label="Andere actie kiezen">
            <ChevronUp size={22} strokeWidth={2.6} />
          </button>
        </div>
        <div className="onderschrift">Actie</div>
        {kan?.stil && (
          <p className="klein zacht midden">Tekenen en Naar Exact staan uit: deze factuur staat in dispuut ({bon.dispuutReden || 'zonder reden'}).</p>
        )}
        {gekozen === 'tekenen' && aan && stap && stap.stap !== 'afronding' && (
          <p className="klein zacht midden">Na het tekenen komt hij bij {aan.naam} te liggen.</p>
        )}
      </section>

      {kiezer === 'gebruiker' && (
        <Kiezer
          titel="Gebruiker" terugLabel="Factuur" zoekbaar
          opties={[{ waarde: '', label: '(Geen gebruiker)' }, ...(personen ?? []).map((p) => ({ waarde: p.id, label: p.naam }))]}
          gekozen={aan?.id ?? ''}
          kies={(w) => setAan(w ? (personen ?? []).find((p) => p.id === w) ?? null : null)}
          sluit={() => setKiezer(null)}
          leegTekst={personen ? 'Niemand gevonden.' : 'Even ophalen…'}
        />
      )}
      {kiezer === 'actie' && (
        <Kiezer
          titel="Acties" terugLabel="Factuur"
          opties={actiesTeKiezen}
          gekozen={gekozen}
          kies={(w) => setActie(w)}
          sluit={() => setKiezer(null)}
        />
      )}
      {kiezer === 'terug' && kan?.terugzetten && (
        <Kiezer
          titel="Terugzetten naar" terugLabel="Factuur"
          opties={kan.terugzetten.map((s) => ({ waarde: s, label: STAPNAAM[s] }))}
          gekozen={null}
          kies={(naar) => setVraag({
            titel: `Terug naar ${STAPNAAM[naar].toLowerCase()}`,
            uitleg: 'De handtekeningen vanaf die stap gaan eraf; die moeten opnieuw. De reden ziet de volgende.',
            label: 'Reden', verplicht: true, knop: 'Terugzetten',
            doe: (reden) => voerUit(async () => {
              await api.terugzetten({ id, verwacht: bon.status, naar, reden, opmerking })
              return `Teruggezet naar ${STAPNAAM[naar].toLowerCase()}.`
            }),
          })}
          sluit={() => setKiezer(null)}
        />
      )}
      {vraag && <Vraag {...vraag} sluit={() => setVraag(null)} />}

      {bewerk && <Bewerker {...bewerk} sluit={() => setBewerk(null)} />}
      {btwOpen && (
        <BtwBewerker pct={bon.vatPct ?? null} bedrag={bon.btwBedrag ?? null} sluit={() => setBtwOpen(false)}
          bewaar={(pct, bedrag) => sla({ vatPct: pct, btwBedrag: bedrag }, { vatPct: bon.vatPct ?? null, btwBedrag: bon.btwBedrag ?? null })} />
      )}
      {kiezer === 'grootboek' && keuzes && (
        <Kiezer
          titel="Grootboekrekening" terugLabel="Factuur" zoekbaar
          opties={keuzes.rekeningen.map((r) => ({ waarde: r.code, label: `${r.code} - ${r.naam}` }))}
          gekozen={bon.grootboekCode ?? null}
          kies={(w) => kiesEnSla({ grootboekCode: w }, { grootboekCode: bon.grootboekCode ?? null })}
          sluit={() => setKiezer(null)}
          leegTekst="Geen rekeningen gevonden voor deze onderneming."
        />
      )}
      {kiezer === 'conditie' && keuzes && (
        <Kiezer
          titel="Betalingsconditie" terugLabel="Factuur"
          opties={[{ waarde: '', label: '(Geen)' }, ...keuzes.condities.map((c) => ({
            waarde: c.code, label: c.naam, uitleg: c.dagen != null ? `${c.dagen} dagen${c.eindVanDeMaand ? ', vanaf het eind van de maand' : ''}` : undefined,
          }))]}
          gekozen={bon.betaalconditie ?? ''}
          kies={(w) => kiesEnSla({ betaalconditie: w || null }, { betaalconditie: bon.betaalconditie ?? null })}
          sluit={() => setKiezer(null)}
        />
      )}
      {kiezer === 'bedrijf' && ik && (
        <Kiezer
          titel="Onderneming" terugLabel="Factuur"
          opties={ik.bedrijven.map((b) => ({ waarde: b.code, label: b.naam }))}
          gekozen={bon.administratie ?? null}
          kies={(w) => {
            if (w === bon.administratie) return
            setKeuzes(null)
            kiesEnSla({ administratie: w }, { administratie: bon.administratie ?? null })
          }}
          sluit={() => setKiezer(null)}
        />
      )}
    </div>
  )
}

function Vak({ waarde, label, zacht, breekt, opKlik }: { waarde: string; label: string; zacht?: boolean; breekt?: boolean; opKlik?: () => void }) {
  const binnen = (
    <>
      <div className={`vak-waarde ${zacht ? 'zacht' : ''} ${breekt ? 'breekt' : ''}`}>{waarde}</div>
      <div className="vak-label">{label}{opKlik && <Pencil size={12} className="potlood" aria-hidden="true" />}</div>
    </>
  )
  /* Een vak dat je kunt aanpassen is een knop: tik erop en het blad komt omhoog. */
  return opKlik
    ? <button type="button" className="vak bewerkbaar" onClick={opKlik} aria-label={`${label} aanpassen`}>{binnen}</button>
    : <div className="vak">{binnen}</div>
}

function Bedrag({ label, waarde, opKlik }: { label: string; waarde: string; opKlik?: () => void }) {
  return opKlik
    /* Een div met role=button: een dt en dd mogen niet in een echte knop. */
    ? <div className="bewerkbaar" role="button" tabIndex={0} onClick={opKlik} aria-label={`${label} aanpassen`}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); opKlik() } }}>
        <dt>{label} <Pencil size={12} className="potlood" aria-hidden="true" /></dt><dd>{waarde}</dd>
      </div>
    : <div><dt>{label}</dt><dd>{waarde}</dd></div>
}

function Gegeven({ label, waarde, opKlik }: { label: string; waarde: string; opKlik?: () => void }) {
  const binnen = (
    <>
      <span className="gegeven-label">{label}</span>
      <span className="gegeven-waarde">{waarde}</span>
      {opKlik && <ChevronRight size={18} className="gegeven-pijl" aria-hidden="true" />}
    </>
  )
  return (
    <li>
      {opKlik
        ? <button type="button" className="gegeven bewerkbaar" onClick={opKlik}>{binnen}</button>
        : <div className="gegeven">{binnen}</div>}
    </li>
  )
}

function Let({ soort, children }: { soort: 'stuk' | 'let-op' | 'info'; children: React.ReactNode }) {
  return (
    <div className={`let let-${soort}`}>
      {soort === 'info' ? <Info size={16} aria-hidden="true" /> : <AlertTriangle size={16} aria-hidden="true" />}
      <span>{children}</span>
    </div>
  )
}
